import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useOverview, useParticipationAnalytics } from '../api';
import { useScope } from '../../store/scope';
import { Stat, PageLoader, ErrorState, StatusBadge } from '../../components/ui';
import { money, num, dateShort, pct } from '../../lib/format';

export function DashboardPage() {
  const { participationId } = useScope();
  const overview = useOverview();
  const scoped = useParticipationAnalytics(participationId, { granularity: 'day' });

  if (overview.isLoading) return <PageLoader />;
  if (overview.error) return <ErrorState message={(overview.error as Error).message} onRetry={() => overview.refetch()} />;
  const o = overview.data!;

  const summary = participationId ? scoped.data?.summary : null;
  const charts = participationId ? scoped.data?.charts : null;

  const revScope = summary?.revenue ?? o.revenue;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Dashboard</h1>
          <p className="text-sm text-ink-400">
            {participationId ? scoped.data?.participation?.expoName ?? 'Selected expo' : 'All exhibitions'}
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Booking value" value={money(revScope.bookingValue)} tone="gold" />
        <Stat label="Confirmed revenue" value={money(revScope.confirmedRevenue)} tone="emerald" />
        <Stat label="Completed revenue" value={money(revScope.completedRevenue)} tone="emerald" />
        <Stat label="Cancelled value" value={money(revScope.cancelledValue)} tone="red" />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Stat label="QR scans" value={num(summary?.scans ?? o.counts.scans)} />
        <Stat label="Unique visitors" value={num(summary?.uniqueVisitors ?? o.counts.visitors)} />
        <Stat label="Product views" value={num(summary?.productViews ?? o.counts.productViews)} />
        <Stat label="Bookings" value={num(summary?.bookings ?? o.counts.bookings)} />
        <Stat
          label="Conversion"
          value={pct(summary?.conversionRatePct ?? null)}
          sub={summary ? 'visitor → booking' : 'select an expo'}
        />
        <Stat label="Avg booking" value={money(revScope.averageBookingValue)} />
      </div>

      {/* Charts (scoped) */}
      {participationId && charts ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Revenue by date">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={charts.revenueByDate ?? []}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#bd9142" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#bd9142" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tickFormatter={(d) => dateShort(d)} fontSize={11} />
                <YAxis fontSize={11} width={64} tickFormatter={(v) => num(v)} />
                <Tooltip formatter={(v) => money(v as number)} labelFormatter={(d) => dateShort(d as string)} />
                <Area type="monotone" dataKey="revenue" stroke="#bd9142" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Bookings & scans by date">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={mergeSeries(charts)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tickFormatter={(d) => dateShort(d)} fontSize={11} />
                <YAxis fontSize={11} width={40} />
                <Tooltip labelFormatter={(d) => dateShort(d as string)} />
                <Bar dataKey="scans" fill="#d0d0d6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="bookings" fill="#161619" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Revenue by stall">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.revenueByStall ?? []} layout="vertical" margin={{ left: 12 }}>
                <XAxis type="number" fontSize={11} tickFormatter={(v) => num(v)} />
                <YAxis type="category" dataKey="stall" fontSize={11} width={60} />
                <Tooltip formatter={(v) => money(v as number)} />
                <Bar dataKey="revenue" fill="#bd9142" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top products by revenue">
            <div className="space-y-2 p-1">
              {(charts.topProducts ?? []).slice(0, 6).map((p: any) => (
                <div key={p.productId} className="flex items-center justify-between text-sm">
                  <span className="min-w-0 truncate">{p.name}</span>
                  <span className="shrink-0 font-medium text-gold-600">{money(p.revenue)}</span>
                </div>
              ))}
              {(charts.topProducts ?? []).length === 0 && (
                <p className="p-2 text-sm text-ink-400">No bookings yet.</p>
              )}
            </div>
          </ChartCard>
        </div>
      ) : (
        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink-700">Top expos by revenue</h2>
          <div className="space-y-2">
            {o.topExpos.map((e) => (
              <Link
                key={e.participationId}
                to={`/admin/participations/${e.participationId}`}
                className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-ink-50"
              >
                <span className="min-w-0 truncate">{e.expoName}</span>
                <span className="shrink-0">
                  <span className="mr-3 text-ink-400">{e.bookings} bookings</span>
                  <span className="font-medium text-gold-600">{money(e.revenue)}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Two-column: upcoming + recent bookings */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink-700">Upcoming & ongoing expos</h2>
          <div className="space-y-2">
            {o.upcoming.length === 0 && <p className="text-sm text-ink-400">Nothing scheduled.</p>}
            {o.upcoming.map((p) => (
              <Link
                key={p.id}
                to={`/admin/participations/${p.id}`}
                className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-ink-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{p.expoName}</span>
                  <span className="text-xs text-ink-400">
                    {dateShort(p.startDate)} – {dateShort(p.endDate)} · {p.city ?? '—'}
                  </span>
                </span>
                <StatusBadge status={p.status} />
              </Link>
            ))}
          </div>
        </div>

        <div className="card p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink-700">Recent bookings</h2>
          <div className="space-y-2">
            {o.recentBookings.map((b) => (
              <div key={b.bookingNumber} className="flex items-center justify-between text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-medium">{b.visitorName}</span>
                  <span className="font-mono text-xs text-ink-400">{b.bookingNumber}</span>
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="font-medium">{money(b.totalAmount)}</span>
                  <StatusBadge status={b.status} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-semibold text-ink-700">{title}</h2>
      {children}
    </div>
  );
}

function mergeSeries(charts: any) {
  const map = new Map<string, any>();
  for (const s of charts.scansByDate ?? []) map.set(s.date, { date: s.date, scans: s.scans, bookings: 0 });
  for (const b of charts.bookingsByDate ?? []) {
    const row = map.get(b.date) ?? { date: b.date, scans: 0, bookings: 0 };
    row.bookings = b.bookings;
    map.set(b.date, row);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}
